/**
 * Pure functions for calculating rolling number display values and positions.
 * These functions are extracted for testability.
 */

export interface UnitDisplayInfo {
    middleDisplayValue: number;
    upperDisplayValue: number;
    lowerDisplayValue: number;
    fractionalOffset: number;
}

/**
 * Complete display state for a single unit
 */
export interface UnitState {
    index: number; // Unit index (0 = leftmost)
    middleText: string; // The rounded/target value (e.g., "20" for value 419)
    upperText: string; // Value above middle (e.g., "30" for value 419)
    lowerText: string; // Value below middle (e.g., "10" for value 419)
    offset: number; // Vertical offset: negative = above baseline, positive = below baseline
    visible: boolean; // Show or hide (for leading zeros)
}

/**
 * Complete display state for the entire rolling number
 */
export interface CompleteDisplayState {
    value: number; // The input value
    precision: number; // The precision setting
    units: UnitState[]; // Display state for each unit, left to right
}

/**
 * Calculate the required digit count based on the value.
 */
export function calculateRequiredDigitCount(
    value: number,
    groupedDigits: number
): number {
    if (value === 0) {
        return groupedDigits; // Minimum to show the grouped unit
    }
    const valueDigits = Math.floor(Math.log10(value)) + 1;
    return Math.max(valueDigits, groupedDigits);
}

/**
 * Calculate the divisor for extracting a digit at a specific unit position.
 */
export function calculateDivisor(
    unitIndex: number,
    unitCount: number,
    digitCount: number,
    precision: number,
    isRightmostUnit: boolean
): number {
    if (isRightmostUnit) {
        return precision;
    }
    // For single digits: extract digit at position (digitCount - 1 - unitIndex) from the right
    return Math.pow(10, digitCount - 1 - unitIndex);
}

/**
 * Calculate what to display for a single unit (non-grouped).
 */
export function calculateSingleDigitDisplay(unitValue: number): {
    middleDisplayValue: number;
    upperDisplayValue: number;
    lowerDisplayValue: number;
} {
    const middleDisplayValue = unitValue % 10;
    const upperDisplayValue = (middleDisplayValue + 1) % 10;
    const lowerDisplayValue = (middleDisplayValue - 1 + 10) % 10;

    return { middleDisplayValue, upperDisplayValue, lowerDisplayValue };
}

/**
 * Calculate what to display for the rightmost grouped unit using rounding.
 */
export function calculateGroupedUnitDisplay(
    rawValue: number,
    precision: number,
    groupedDigits: number
): {
    middleDisplayValue: number;
    upperDisplayValue: number;
    lowerDisplayValue: number;
} {
    // Extract last groupedDigits, then round to nearest precision
    const lastDigits = rawValue % Math.pow(10, groupedDigits);
    const middleDisplayValue = Math.round(lastDigits / precision) * precision;

    // Upper is the next higher value (wraps)
    const maxGroupValue = Math.pow(10, groupedDigits);
    const upperDisplayValue = (middleDisplayValue + precision) % maxGroupValue;

    // Lower is the next lower value (wraps)
    const lowerDisplayValue =
        (middleDisplayValue - precision + maxGroupValue) % maxGroupValue;

    return { middleDisplayValue, upperDisplayValue, lowerDisplayValue };
}

/**
 * Calculate the complete display information for a single unit.
 */
export function calculateUnitDisplay(
    unitIndex: number,
    rawValue: number,
    digitCount: number,
    unitCount: number,
    precision: number,
    groupedDigits: number
): UnitDisplayInfo {
    const isRightmostUnit = unitIndex === unitCount - 1;
    // Calculate divisor and extract value
    const divisor = calculateDivisor(
        unitIndex,
        unitCount,
        digitCount,
        precision,
        isRightmostUnit
    );
    const unitValue = Math.floor(rawValue / divisor);

    // Calculate display values
    let displayInfo: {
        middleDisplayValue: number;
        upperDisplayValue: number;
        lowerDisplayValue: number;
    };

    if (isRightmostUnit) {
        displayInfo = calculateGroupedUnitDisplay(
            rawValue,
            precision,
            groupedDigits
        );
    } else {
        displayInfo = calculateSingleDigitDisplay(unitValue);
    }

    let fractionalOffset = 0;
    if (isRightmostUnit) {
        const lastDigits = rawValue % Math.pow(10, groupedDigits);
        fractionalOffset =
            (lastDigits - displayInfo.middleDisplayValue) / precision;
    }

    return {
        ...displayInfo,
        fractionalOffset,
    };
}

/**
 * Check if a unit should be hidden as a leading zero.
 */
export function shouldHideAsLeadingZero(
    unitIndex: number,
    rawValue: number,
    digitCount: number,
    unitCount: number
): boolean {
    // Always show the rightmost unit
    if (unitIndex >= unitCount - 1) {
        return false;
    }

    // Check if this unit and all units before it are zero
    for (let j = 0; j <= unitIndex; j++) {
        const checkDivisor = Math.pow(10, digitCount - 1 - j);
        const checkUnitValue = Math.floor(rawValue / checkDivisor) % 10;
        if (checkUnitValue !== 0) {
            return false;
        }
    }

    return true;
}

/**
 * Calculate the complete display state for a rolling number display.
 * This is the main entry point for determining what to render.
 *
 * @param value - The numeric value to display
 * @param precision - The precision (e.g., 10 means round to nearest 10)
 * @returns Complete display state with all units and their properties
 */
export function calculateCompleteDisplayState(
    value: number,
    precision: number = 1
): CompleteDisplayState {
    const rawValue = Math.max(0, value);
    const groupedDigits = precision.toString().length;
    const digitCount = calculateRequiredDigitCount(rawValue, groupedDigits);
    const unitCount = digitCount - groupedDigits + 1;

    const units: UnitState[] = [];

    for (let unitIndex = 0; unitIndex < unitCount; unitIndex++) {
        const isRightmostUnit = unitIndex === unitCount - 1;

        // Calculate display info using existing functions
        const displayInfo = calculateUnitDisplay(
            unitIndex,
            rawValue,
            digitCount,
            unitCount,
            precision,
            groupedDigits
        );

        // Format text based on whether it's grouped or single digit
        const padLength = isRightmostUnit ? groupedDigits : 1;

        // For rightmost unit, wrap middleDisplayValue if it exceeds the max group value
        // (e.g., rounding 99 gives 100, which should wrap to 0 for 2-digit display)
        let middleValue = displayInfo.middleDisplayValue;
        if (isRightmostUnit && middleValue >= Math.pow(10, groupedDigits)) {
            middleValue = middleValue % Math.pow(10, groupedDigits);
        }

        const middleText = middleValue.toString().padStart(padLength, '0');
        const upperText = displayInfo.upperDisplayValue
            .toString()
            .padStart(padLength, '0');
        const lowerText = displayInfo.lowerDisplayValue
            .toString()
            .padStart(padLength, '0');

        // Check visibility
        const visible = !shouldHideAsLeadingZero(
            unitIndex,
            rawValue,
            digitCount,
            unitCount
        );

        units.push({
            index: unitIndex,
            middleText,
            upperText,
            lowerText,
            offset: displayInfo.fractionalOffset,
            visible,
        });
    }

    return {
        value: rawValue,
        precision,
        units,
    };
}
